import { defineStore } from 'pinia';
import { apiClient } from '@/lib/api/client';
import { CouponTypes } from '@/lib/api/coupons.api';
import couponTypeDefs from '@/utils/coupon-types';

/** Scanner UI order (checkboxes + type picker). Unknown codes sort after, by resolved key. */
const COUPON_TYPE_SCANNER_DISPLAY_ORDER = [
  'CDI',
  'CHO',
  'CPP',
  'CCC',
  'CPC',
  'CCT',
  'ECR',
  'CAC',
] as const;

const ORDER = COUPON_TYPE_SCANNER_DISPLAY_ORDER as readonly string[];

const LABEL_TO_CODE = new Map<string, string>(
  couponTypeDefs.map((d) => [d.label, d.code] as [string, string]),
);

function normalizeCouponTypeCode(code: unknown): string {
  return String(code ?? '').trim().toUpperCase();
}

/**
 * Best-effort acronym used for ordering: match `code` to known order, else infer from exact `label`
 * (see `utils/coupon-types.ts`), else use normalized `code` for stable “unknown” ordering.
 */
function acronymForScannerOrder(t: CouponTypes): string {
  const codeNorm = normalizeCouponTypeCode(t.code);
  if (codeNorm && ORDER.includes(codeNorm)) {
    return codeNorm;
  }

  const label = t.label != null ? String(t.label).trim() : '';
  const fromLabel = label ? LABEL_TO_CODE.get(label) : undefined;
  if (fromLabel) {
    return fromLabel;
  }

  return codeNorm;
}

function sortCouponTypesForScannerDisplay(types: CouponTypes[]): CouponTypes[] {
  if (!Array.isArray(types) || types.length === 0) {
    return Array.isArray(types) ? [...types] : [];
  }

  const rank = (t: CouponTypes): number => {
    const key = normalizeCouponTypeCode(acronymForScannerOrder(t));
    const idx = ORDER.indexOf(key);
    return idx === -1 ? ORDER.length : idx;
  };

  return [...types].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) {
      return ra - rb;
    }
    const ka = normalizeCouponTypeCode(acronymForScannerOrder(a));
    const kb = normalizeCouponTypeCode(acronymForScannerOrder(b));
    return ka.localeCompare(kb);
  });
}

export const useCouponTypesStore = defineStore('couponTypes', {
  state: () => ({
    types: [] as CouponTypes[],
    loading: false,
    error: null as Error | null,
  }),

  getters: {
    /** Types sorted for scanner filter + coupon type picker (CDI … CAC, then others). */
    allTypes: (state): CouponTypes[] => sortCouponTypesForScannerDisplay(state.types),

    activeTypes(): CouponTypes[] {
      return sortCouponTypesForScannerDisplay(this.types.filter((type) => type.active));
    },

    getTypeByCode: (state) => {
      return (code: string): CouponTypes | undefined => {
        return state.types.find((type) => type.code === code);
      };
    },

    getTypeById: (state) => {
      return (id: number): CouponTypes | undefined => {
        return state.types.find((type) => type.id === id);
      };
    },
  },

  actions: {
    async fetchCouponTypes(): Promise<void> {
      try {
        this.loading = true;
        this.error = null;

        const { data, response } = await apiClient.GET('/coupons/types');

        if (!response.ok) {
          throw new Error('Failed to fetch coupon types');
        }

        this.types = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error(error);
        this.error = error as Error;
      } finally {
        this.loading = false;
      }
    },
  },
});
